import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, User, HandHeart, Key, Eye, EyeOff } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';

const LendCharacterModal = ({
  isOpen,
  onClose,
  onConfirm,
  characters = [],
  currentParticipations = [],
  loading: externalLoading = false
}) => {
  const [selectedCharacterId, setSelectedCharacterId] = useState('');
  const [pilotOption, setPilotOption] = useState(''); // 'has_pilot' | 'no_pilot'
  const [pilotName, setPilotName] = useState('');
  const [loginId, setLoginId] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = select character, 2 = pilot options

  useEffect(() => {
    if (isOpen) {
      setSelectedCharacterId('');
      setPilotOption('');
      setPilotName('');
      setLoginId('');
      setTempPassword('');
      setShowPassword(false);
      setStep(1);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (step === 1 && selectedCharacterId) {
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setPilotOption('');
      setPilotName('');
      setLoginId('');
      setTempPassword('');
    }
  };

  const canSubmit = () => {
    if (!selectedCharacterId || !pilotOption) return false;
    if (pilotOption === 'has_pilot' && !pilotName.trim()) return false;
    if (pilotOption === 'no_pilot' && (!loginId.trim() || !tempPassword.trim())) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;
    setLoading(true);
    try {
      await onConfirm({
        characterId: selectedCharacterId,
        pilot: pilotOption === 'has_pilot' ? pilotName.trim() : null,
        lentHasPilot: pilotOption === 'has_pilot',
        lentLoginId: pilotOption === 'no_pilot' ? loginId.trim() : null,
        lentTempPassword: pilotOption === 'no_pilot' ? tempPassword.trim() : null,
      });
      onClose();
    } catch (error) {
      console.error('Error lending character:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedCharacter = characters.find(c => c.id === selectedCharacterId);

  // Filter out characters that are already confirmed
  const confirmedCharIds = currentParticipations
    .filter(p => p.status === 'CONFIRMED' || p.status === 'CHARACTER_LENT')
    .map(p => p.characterId);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-lg max-h-[90vh] overflow-hidden"
        >
          <Card variant="glass" className="relative">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <HandHeart className="w-6 h-6 text-amber-400" />
                  Emprestar Personagem
                </h3>
                <p className="text-sm text-gray-400 mt-1">
                  {step === 1
                    ? 'Selecione o personagem que deseja emprestar'
                    : 'Informe sobre o piloto'
                  }
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Step 1: Select Character */}
              {step === 1 && (
                <div className="space-y-3">
                  {characters.length === 0 ? (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-400">Você não tem personagens cadastrados</p>
                    </div>
                  ) : (
                    characters.map(character => {
                      const isAlreadyParticipating = confirmedCharIds.includes(character.id);
                      const isSelected = selectedCharacterId === character.id;
                      const lentParticipation = currentParticipations.find(
                        p => p.characterId === character.id && p.status === 'CHARACTER_LENT'
                      );

                      return (
                        <div
                          key={character.id}
                          className={`border rounded-xl p-4 transition-all ${
                            isAlreadyParticipating
                              ? 'border-white/5 bg-white/5 opacity-50 cursor-not-allowed'
                              : isSelected
                              ? 'border-amber-500/50 bg-amber-500/10 cursor-pointer'
                              : 'border-white/10 bg-white/5 hover:border-white/20 hover:bg-white/10 cursor-pointer'
                          }`}
                          onClick={() => !isAlreadyParticipating && setSelectedCharacterId(character.id)}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                isSelected
                                  ? 'border-amber-500 bg-amber-500'
                                  : 'border-gray-400'
                              }`}
                            >
                              {isSelected && <Check className="w-4 h-4 text-white" />}
                            </div>

                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                              {character.name?.[0] || '?'}
                            </div>

                            <div className="flex-1">
                              <h4 className="font-semibold text-white">{character.name}</h4>
                              <p className="text-sm text-gray-400">
                                {character.class} • Nível {character.level}
                              </p>
                            </div>

                            {isAlreadyParticipating && (
                              <div className="px-3 py-1 rounded-lg text-xs font-medium bg-green-500/20 text-green-400">
                                {lentParticipation ? 'Emprestado' : 'Confirmado'}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Step 2: Pilot Options */}
              {step === 2 && selectedCharacter && (
                <div className="space-y-4">
                  {/* Selected character summary */}
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center text-white font-bold">
                      {selectedCharacter.name?.[0] || '?'}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{selectedCharacter.name}</p>
                      <p className="text-xs text-gray-400">
                        {selectedCharacter.class} • Nível {selectedCharacter.level}
                      </p>
                    </div>
                  </div>

                  {/* Pilot options */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-300">
                      Situação do piloto:
                    </label>

                    {/* Option: Has pilot */}
                    <div
                      className={`border rounded-xl p-4 transition-all cursor-pointer ${
                        pilotOption === 'has_pilot'
                          ? 'border-green-500/50 bg-green-500/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      onClick={() => setPilotOption('has_pilot')}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            pilotOption === 'has_pilot'
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-400'
                          }`}
                        >
                          {pilotOption === 'has_pilot' && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <p className="font-medium text-white">Já tenho piloto</p>
                          <p className="text-xs text-gray-400">Outra pessoa já vai pilotar meu personagem</p>
                        </div>
                      </div>
                    </div>

                    {/* Pilot name input (only if has_pilot) */}
                    {pilotOption === 'has_pilot' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="ml-8"
                      >
                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                          <p className="text-xs text-yellow-300 flex items-start gap-2">
                            <Key className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span>
                            <strong>ATENÇÃO:</strong> Ao selecionar e preencher essa informação é de sua TOTAL responsabilidade contatar ele e passar sua ID e senha.
                          </span>
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Nome do piloto
                          </label>
                          <input
                              type="text"
                              value={pilotName}
                              onChange={(e) => setPilotName(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Digite o nome do piloto..."
                              className="w-full px-4 py-3 bg-slate-700/50 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          />
                        </div>
                      </motion.div>
                    )}

                    {/* Option: No pilot */}
                    <div
                      className={`border rounded-xl p-4 transition-all cursor-pointer ${
                        pilotOption === 'no_pilot'
                          ? 'border-orange-500/50 bg-orange-500/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      onClick={() => setPilotOption('no_pilot')}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            pilotOption === 'no_pilot'
                              ? 'border-orange-500 bg-orange-500'
                              : 'border-gray-400'
                          }`}
                        >
                          {pilotOption === 'no_pilot' && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div>
                          <p className="font-medium text-white">Não tenho piloto</p>
                          <p className="text-xs text-gray-400">Informar credenciais para um admin designar o piloto</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Credential fields (only if no_pilot) */}
                  {pilotOption === 'no_pilot' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-3"
                    >
                      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                        <p className="text-xs text-red-300 flex items-start gap-2">
                          <Key className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>
                            <strong>Dados seguros:</strong> Suas credenciais serão criptografadas e visíveis apenas para administradores.
                            Nenhum membro comum terá acesso a essas informações.<br/>
                            Mesmo diante disso é necessário ter cautela e só fornecer credenciais temporárias, com itens trancados baú e distribuidor livres para repotar.
                          </span>
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          ID de Login da conta
                        </label>
                        <input
                          type="text"
                          value={loginId}
                          onChange={(e) => setLoginId(e.target.value)}
                          placeholder="Digite o ID de login..."
                          className="w-full px-4 py-3 bg-slate-700/50 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Senha temporária
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={tempPassword}
                            onChange={(e) => setTempPassword(e.target.value)}
                            placeholder="Digite a senha temporária..."
                            className="w-full px-4 py-3 pr-12 bg-slate-700/50 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 p-6 border-t border-white/10">
              <div>
                {step === 2 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    disabled={loading || externalLoading}
                  >
                    Voltar
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={loading || externalLoading}
                >
                  Cancelar
                </Button>

                {step === 1 ? (
                  <Button
                    onClick={handleNext}
                    disabled={!selectedCharacterId}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit() || loading || externalLoading}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                  >
                    {loading || externalLoading ? (
                      <>Emprestando...</>
                    ) : (
                      <>
                        <HandHeart className="w-4 h-4 mr-2" />
                        Confirmar Empréstimo
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LendCharacterModal;

